import { Grid } from '@material-ui/core';
import { advance, aggregateEvents } from '@onward-delivery/core';
import React, { useMemo } from 'react';
import { Anchor, Body1, Header1, Step, SubStepDesktop, SubStepMobile, Tag } from './blocks';
import { css } from '@emotion/react';
import { formatInTimeZone } from 'date-fns-tz';
import { useClientUser } from '@/hooks';
import { middleMileStepTimes } from './utils';

const StepLabel = ({ title, timeLabel, time, tz, onEdit }) => (
    <Grid
        direction="row"
        container
        css={css`
            align-items: center;
        `}
    >
        <Grid
            direction="column"
            css={css`
                margin-right: 8px;
            `}
        >
            <Header1>{title}</Header1>
        </Grid>
        {time && (
            <Grid direction="column">
                <Tag>
                    <Body1
                        css={css`
                            margin-right: 20px;
                        `}
                    >
                        {`${timeLabel}: ${formatInTimeZone(new Date(time), tz, 'EEE, MMM dd, yyyy, h:mm a zzz')}`}
                    </Body1>
                    {onEdit && (
                        <Anchor onClick={onEdit}>
                            <Header1>Edit</Header1>
                        </Anchor>
                    )}
                </Tag>
            </Grid>
        )}
    </Grid>
);

export default function MiddleMileSteps({
    isDesktop,
    isLoading,
    warehouse,
    readOnly,
    before,
    after,
    order,
    tz,
    callbacks,
}) {
    const SCHEDULE_STEPS = [
        {
            description: `Schedule middle mile pickup from shipper origin`,
            complete: ({ order, before }) => {
                return order.wh_events.some((event) => event.transition?.next === 'MM_CD_PENDING');
            },
            ...(!readOnly
                ? {
                      cta: 'Schedule Pickup',
                      callback: ({ order, before }) => {
                          let events = advance(order, order.event_state, 'MM_CD_PENDING', { mmIsCrossdock: true });

                          callbacks.editReceiveEvent({
                              events,
                              action: 'AWAITING:ADD_MM_CD',
                              timestampLabel: 'Est Ship Date',
                              timestampAttr: 'mm_est_ship_date',
                              description1: '',
                              description2: '',
                              params: { status: 'NOT_DELIVERED' },
                          });
                      },
                  }
                : {}),
        },
    ];
    const IN_TRANSIT_STEPS = [
        {
            description: `Mark middle mile status as 'In-transit'`,
            complete: ({ order, before }) => {
                return order.wh_events.some((event) => event.transition?.next === 'MM_CD_IN_TRANSIT');
            },
            ...(!readOnly
                ? {
                      cta: 'Complete Pickup',
                      callback: ({ order, before }) => {
                          let events = advance(order, order.event_state, 'MM_CD_IN_TRANSIT', { mmIsCrossdock: true });

                          callbacks.editReceiveEvent({
                              events,
                              action: 'MM_CD_PENDING:PU_SUCCESS',
                              timestampLabel: 'Pickup Date',
                              timestampAttr: 'mm_ship_date',
                              description1: '',
                              description2: '',
                              params: { status: 'NOT_DELIVERED' },
                          });
                      },
                  }
                : {}),
        },
    ];
    const ARRIVED_STEPS = [
        {
            description: `Mark middle mile status as 'Arrived'`,
            complete: ({ order, before }) => {
                return order.wh_events.some((event) => event.transition?.next === 'MM_CD_ARRIVED');
            },
            ...(!readOnly
                ? {
                      cta: 'Complete Delivery',
                      secondaryCta: 'Add Exception',
                      callback: ({ order, before }) => {
                          let events = advance(order, order.event_state, 'MM_CD_ARRIVED', { mmIsCrossdock: true });

                          callbacks.editReceiveEvent({
                              events,
                              action: 'MM_CD_OUT_FOR_DELIVERY:MM_ARRIVED',
                              timestampLabel: 'Delivery Date',
                              timestampAttr: 'arrived_date',
                              description1: '',
                              description2: '',
                              params: { status: 'NOT_DELIVERED' },
                          });
                      },
                      secondaryCallback: ({ order, before }) => {
                          callbacks.createException({
                              type: 'PICKUP',
                              action: 'START:AWAITING',
                          });
                      },
                  }
                : {}),
        },
    ];

    const times = useMemo(() => middleMileStepTimes(order, before), [order, before]);
    // The step's Edit link reopens the same modal as its CTA, so it exists only when the CTA does.
    const editStep = ([{ callback }]) => (callback ? () => callback({ order, before }) : null);

    const [dispatchIsComplete, scheduledIsComplete, inTransitIsComplete, arrivedIsComplete] = useMemo(() => {
        const overrideComplete = order.wh_events.some((event) =>
            ['AWAITING:RECEIVED', 'MM_CD_ARRIVED:CONFIRMED'].includes(event.action)
        );
        return [
            [true],
            SCHEDULE_STEPS.map((s) => overrideComplete || s.complete({ order, before })),
            IN_TRANSIT_STEPS.map((s) => overrideComplete || s.complete({ order, before })),
            ARRIVED_STEPS.map((s) => overrideComplete || s.complete({ order, before })),
        ];
    }, [order, before]);

    // Skip MM, go AWAITING -> RECEIVING directly
    if (!order.has_middle_mile_origin) return null;

    // Booked is when the carrier's system created the order, which only an integration reports.
    const hasIntegration = !!order.middle_mile_integration_type;
    const bookedIsComplete =
        !!times.booked ||
        [scheduledIsComplete, inTransitIsComplete, arrivedIsComplete].some((steps) => steps.every((x) => !!x));

    return (
        <>
            {hasIntegration && (
                <Step
                    isDesktop={isDesktop}
                    isComplete={bookedIsComplete}
                    inProgress={!bookedIsComplete}
                    label={() => (
                        <StepLabel title="Middle Mile Booked" timeLabel="Booked" time={times.booked} tz={tz} />
                    )}
                >
                    {() => <></>}
                </Step>
            )}
            <Step
                isDesktop={isDesktop}
                isComplete={scheduledIsComplete.every((x) => !!x)}
                inProgress={scheduledIsComplete.some((x) => !x) && dispatchIsComplete.every((x) => !!x)}
                label={() => (
                    <StepLabel
                        title="Middle Mile Pickup Scheduled"
                        timeLabel="Scheduled"
                        time={times.scheduled}
                        tz={tz}
                        onEdit={editStep(SCHEDULE_STEPS)}
                    />
                )}
            >
                {() => (
                    <>
                        {SCHEDULE_STEPS.map(({ description, cta, secondaryCta, callback, secondaryCallback }, idx) => {
                            const SubStep = isDesktop ? SubStepDesktop : SubStepMobile;
                            const inProgressIdx = scheduledIsComplete.findIndex((x) => !x);
                            return (
                                <SubStep
                                    key={idx}
                                    isComplete={scheduledIsComplete[idx]}
                                    inProgress={inProgressIdx === idx}
                                    isLoading={isLoading}
                                    isLast={idx === SCHEDULE_STEPS.length - 1}
                                    description={description}
                                    cta={cta}
                                    secondaryCta={secondaryCta}
                                    callback={
                                        callback
                                            ? () => {
                                                  callback({ before, after, order });
                                              }
                                            : null
                                    }
                                    secondaryCallback={secondaryCallback}
                                    secondaryOrange={true}
                                />
                            );
                        })}
                    </>
                )}
            </Step>
            <Step
                isDesktop={isDesktop}
                isComplete={inTransitIsComplete.every((x) => !!x)}
                inProgress={inTransitIsComplete.some((x) => !x) && scheduledIsComplete.every((x) => !!x)}
                label={() => (
                    <StepLabel
                        title="Middle Mile In-Transit"
                        timeLabel="In Transit"
                        time={times.inTransit}
                        tz={tz}
                        onEdit={editStep(IN_TRANSIT_STEPS)}
                    />
                )}
            >
                {() => (
                    <>
                        {IN_TRANSIT_STEPS.map(
                            ({ description, cta, secondaryCta, callback, secondaryCallback }, idx) => {
                                const SubStep = isDesktop ? SubStepDesktop : SubStepMobile;
                                const inProgressIdx = inTransitIsComplete.findIndex((x) => !x);
                                return (
                                    <SubStep
                                        key={idx}
                                        isComplete={inTransitIsComplete[idx]}
                                        inProgress={inProgressIdx === idx}
                                        isLoading={isLoading}
                                        isLast={idx === IN_TRANSIT_STEPS.length - 1}
                                        description={description}
                                        cta={cta}
                                        secondaryCta={secondaryCta}
                                        callback={
                                            callback
                                                ? () => {
                                                      callback({ before, after, order });
                                                  }
                                                : null
                                        }
                                        secondaryCallback={secondaryCallback}
                                        secondaryOrange={true}
                                    />
                                );
                            }
                        )}
                    </>
                )}
            </Step>
            <Step
                isDesktop={isDesktop}
                isComplete={arrivedIsComplete.every((x) => !!x)}
                inProgress={arrivedIsComplete.some((x) => !x) && inTransitIsComplete.every((x) => !!x)}
                label={() => (
                    <StepLabel
                        title="Middle Mile Arrived"
                        timeLabel="Arrived"
                        time={times.arrived}
                        tz={tz}
                        onEdit={editStep(ARRIVED_STEPS)}
                    />
                )}
            >
                {() => (
                    <>
                        {ARRIVED_STEPS.map(({ description, cta, secondaryCta, callback, secondaryCallback }, idx) => {
                            const SubStep = isDesktop ? SubStepDesktop : SubStepMobile;
                            const inProgressIdx = arrivedIsComplete.findIndex((x) => !x);
                            return (
                                <SubStep
                                    key={idx}
                                    isComplete={arrivedIsComplete[idx]}
                                    inProgress={inProgressIdx === idx}
                                    isLoading={isLoading}
                                    isLast={idx === ARRIVED_STEPS.length - 1}
                                    description={description}
                                    cta={cta}
                                    secondaryCta={secondaryCta}
                                    callback={
                                        callback
                                            ? () => {
                                                  callback({ before, after, order });
                                              }
                                            : null
                                    }
                                    secondaryCallback={secondaryCallback}
                                    secondaryOrange={true}
                                />
                            );
                        })}
                    </>
                )}
            </Step>
        </>
    );
}
